<template>
  <div>
    <Subheader title="Select Payment Method" />

    <div v-if="dataLoaded" class="app-content-column">
      <div class="card">
        <h3>Thank you for your donation.</h3>
        <p>
          You are donating {{ amount | formatNumberAsUSD }} to the milestone "{{ goal.title }}",
          plus {{ (amount * 0.1) | formatNumberAsUSD }} to MzingoPartners.com to facilitate this
          donation.
        </p>
      </div>

      <div v-if="!showNewCardPanel" class="card" style="position: relative">
        <h5>Saved Payment Methods</h5>
        <div
          v-for="paymentMethod in paymentMethods"
          :key="paymentMethod.id"
          class="form__element"
          style="margin-left: 0"
        >
          <input
            :id="paymentMethod.id"
            v-model="selectedPaymentMethod"
            class="radio"
            type="radio"
            name="checkbox-options"
            :value="paymentMethod"
            :checked="paymentMethod.id === defaultPaymentMethod"
          />
          <label :for="paymentMethod.id">
            Card Ending in {{ paymentMethod.last4 }} ({{ paymentMethod.exp_month }}/{{
              paymentMethod.exp_year
            }})
            <span v-if="paymentMethod.id === defaultPaymentMethod" class="star">*</span>
          </label>
        </div>
        <h6 @click="toggleNewCardPanel">Or add a new card</h6>
        <p class="content" style="position: absolute; right: 1.75rem; bottom: -.75rem;">
          <span class="star">*</span> default
        </p>
      </div>

      <div v-if="showNewCardPanel" class="card">
        <h5>Add New Card</h5>
        <form @submit.stop.prevent="submitNewPaymentMethod">
          <StripeForm @stripe-token-returned="addPaymentMethod($event)" />
        </form>
        <h6 v-if="paymentMethods.length > 0" @click="toggleNewCardPanel">Use Saved Card</h6>
      </div>

      <div v-if="selectedPaymentMethod" class="confirm-container">
        <div class="button-wrapper is-centered">
          <button class="large-button" :disabled="loading" @click="makePayment(goal.id, amount)">
            {{ loading ? 'Processing...' : 'Confirm Payment' }}
          </button>
        </div>
        <p>
          Once you confirm, we will charge {{ (amount * 1.1) | formatNumberAsUSD }} to the card
          ending in {{ selectedPaymentMethod.last4 }}. This includes a 10% donation to
          MzingoPartners.com
        </p>
      </div>
    </div>
  </div>
</template>

<script>
/*global fbq*/
import Subheader from '@/components/Subheader'
import StripeForm from '@/components/StripeForm'

import User from '@/services/users'
import Goal from '@/services/goals'

import { mapState } from 'vuex'

export default {
  name: 'PaymentSetup',
  components: {
    Subheader,
    StripeForm,
  },
  data() {
    return {
      dataLoaded: false,
      paymentMethods: [],
      defaultPaymentMethod: null,
      showNewCardPanel: false,
      selectedPaymentMethod: {},
      goalId: null,
      amount: null,
      goal: {},
      loading: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  created() {
    // NRS 8.29.19 - TODO: Update this so the goalId and amount are not passed in through the query params. I passed them in
    // as props but they were not persisting when the page refreshed, which threw an error. So this was a quick fix.
    this.goalId = this.$route.params.goalId
    this.amount = this.$route.params.amount
    this.refreshData()
  },
  methods: {
    refreshData(togglePanel = false) {
      // NRS 8.29.19 - togglePanel just resets the panel when a new card is added.
      let goalPromise = Goal.api.get(this.goalId).then(data => {
        this.goal = data
      })
      let paymentMethodsPromise = User.api.getPaymentMethods(this.user.id).then(data => {
        this.paymentMethods = data.results
        if (this.paymentMethods.length === 0) {
          this.toggleNewCardPanel()
        }
      })
      let defaultPaymentMethodPromise = User.api
        .getDefaultPaymentMethod(this.user.id)
        .then(data => {
          this.defaultPaymentMethod = data
        })
      Promise.all([paymentMethodsPromise, goalPromise, defaultPaymentMethodPromise]).then(() => {
        this.selectedPaymentMethod = Array.isArray(this.paymentMethods)
          ? this.paymentMethods.find(
              paymentMethod => paymentMethod.id === this.defaultPaymentMethod,
            )
          : null
        this.dataLoaded = true
        if (togglePanel) {
          this.toggleNewCardPanel()
        }
      })
    },
    toggleNewCardPanel() {
      this.showNewCardPanel = !this.showNewCardPanel
    },
    addPaymentMethod(stripeToken) {
      User.api.addPaymentMethod(this.user.id, stripeToken.id).then(data => {
        this.$Alert.alert({
          message: `
          <h3>Card Added</h3>
          `,
          timeout: 5000,
        })
        // NRS 8.29.19 - there's a cleaner way to do this. Right now, I add the payment method and then update the default payment
        // method. We should eventually add a "add as default" flag on the form. Right now, every new payment method automatically
        // becomes the default.
        User.api.updateDefaultPaymentMethod(this.user.id, data.results.id).then(() => {
          this.refreshData(true)
        })
      })
    },
    async makePayment(goal, amount) {
      this.sendConfirmPaymentGA()

      this.loading = true
      const amountInCents = amount * 100

      try {
        // Facebook Pixel tracking code
        fbq('track', 'Donate')

        await Goal.api.makePayment(goal, amountInCents)
        this.$Alert.alert({
          type: 'success',
          message: '<h3>Payment submitted!</h3>',
          timeout: 5000,
        })
        this.$router.push({ name: 'Project', params: { projectId: this.goal.project } })
      } catch (e) {
        this.$Alert.alert({
          type: 'error',
          message: '<h3>There was a problem submitting your payment. Please try again later.</h3>',
          timeout: 6000,
        })
      } finally {
        this.loading = false
      }
    },
    /** Google Analytics - Send "Click 'Confirm Payment'" event to GA. */
    sendConfirmPaymentGA() {
      this.$ga.event({
        eventCategory: "Click 'Confirm Payment'",
        eventAction: 'From Payment Setup',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/mixins/cards';
@import '../styles/mixins/buttons';
@import '../styles/forms';
@import '../styles/layout';

.card {
  @include card-w-borders();
}

.star {
  font-size: 1rem;
  font-weight: bold;
  color: $primary;
}

.text {
  padding: 0 2rem;
}

.confirm-container {
  text-align: center;

  p {
    display: inline-block;
    max-width: 500px;
  }
}
</style>
